<template>
  <form @submit.prevent="submit">
    <b-row>
      <b-col>
        <b-card :title="$t('companies.general')">
          <fields ungrouped />

          <fields
            v-if="model.has_city_taxi"
            group="city_taxi"
            layout="horizontal"
            class="service_areas"
          />

          <fields
            v-if="model.has_delivery"
            group="delivery"
            layout="horizontal"
            class="service_areas"
          />
        </b-card>
        <b-card v-if="user" :title="$t('companies.user_data')">
          <p>{{ $t('companies.asociated_user') }}:</p>
          <div>
            <b>{{ $t('companies.user_id') }}:</b> {{ user.id }}
          </div>
          <div>
            <b>{{ $t('companies.user_email') }}:</b> {{ user.email }}
          </div>
          <template slot="footer">
            <router-link
              v-if="$allows('users.update')"
              :to="{ name: 'users.edit', params: { id: user.id } }"
              class="btn btn-link"
            >
              {{ $t('companies.edit_user_credentials') }}
            </router-link>
            <router-link v-else :to="{ name: 'account' }" class="btn btn-link">
              {{ $t('companies.edit_account_settings') }}
            </router-link>
          </template>
        </b-card>

        <b-card :title="$t('companies.title_vehicle_types')">
          <table class="table">
            <thead>
              <tr>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td
                  v-for="rideType in rideTypes"
                  :key="rideType"
                  class="text-center"
                  v-text="rideType"
                />
              </tr>
            </thead>
            <tbody>
              <tr v-for="vehicle in vehicles" :key="vehicle.id">
                <td class="text-center">
                  <div>
                    <img
                      v-if="vehicle.image && vehicle.image"
                      :src="thumb(vehicle.image, 120)"
                      :alt="vehicle.name"
                      style="width: 120px"
                    />
                  </div>
                  {{ vehicle.name }} ({{ vehicle.division.label }})
                </td>
                <td>
                  <template v-for="(value, attr) in vehicle.attributes">
                    <p v-if="value" :key="attr" class="text-nowrap">
                      {{ $t(`vehicles.attributes.${attr}.label`) }}:
                      {{ value }}
                    </p>
                  </template>
                </td>
                <td
                  v-for="rideType in rideTypes"
                  :key="rideType"
                  class="text-center"
                >
                  <input
                    v-if="vehicle.ride_types.includes(rideType)"
                    :key="`${vehicle.id}_${rideType}`"
                    v-model="checked[rideType]"
                    :value="vehicle.id"
                    type="checkbox"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </b-card>
      </b-col>
      <b-col>
        <b-card :title="$t('companies.billing')">
          <fields group="billing" />
        </b-card>
        <b-card :title="$t('company.contract')">
          <div v-if="contract">
            <a
              :href="contract.url"
              target="_blank"
              v-text="$t('general.download')"
            />
            <p class="text-muted font-italic">
              <value-date :item="contract" attribute="uploaded_at" />
            </p>
          </div>
          <div>
            <!-- eslint-disable-next-line vue/no-v-html -->
            <p v-html="contractHelpText" />
            <fields group="contract" layout="vertical" />
          </div>
        </b-card>
        <b-card :title="$t('companies.notifications')">
          <fields group="notifications" />
        </b-card>
        <b-card v-if="showAirports" :title="$t('companies.airports')">
          <AirportsList :airports="airports" @delete="deleteAirport" />
          <NewAirport v-model="newAirportModel" @submit="addAirport" />
        </b-card>

        <b-card
          v-if="$allows('administer')"
          :title="$t('companies.not_cooperate')"
        >
          <fields group="cooperate" layout="vertical" />
        </b-card>
      </b-col>
    </b-row>
    <div>
      <b-button type="submit" variant="primary">
        {{ $t('general.save') }}
      </b-button>
      <b-button
        v-if="showCancelButton"
        type="button"
        variant="light"
        class="ml-2"
        @click="cancel"
      >
        {{ $t('general.cancel') }}
      </b-button>
    </div>
  </form>
</template>

<script>
import { ResourceFormMixin } from 'stylemix-vue-admin'
import { Field } from 'stylemix-base'
import CompaniesApi from './Api'
import VehiclesApi from '../vehicles/Api'
import AirportsApi from '../airports/companies/Api'
import ListZones from '../components/timezones.json'
import ValueDate from '../components/values/ValueDate'
import AirportsList from './AirportsList'
import NewAirport from './NewAirport'

export default {
  name: 'CompanyForm',

  components: {
    AirportsList,
    NewAirport,
    ValueDate,
  },
  mixins: [ResourceFormMixin],
  props: {
    showCancelButton: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      base: 'companies',
      contract: null,
      contractTemplateUrl: null,
      user: null,
      vehicles: [],
      selected: false,
      checked: {},
      timezones: [],
      rideTypes: [],
      airports: [],
      newAirportModel: {
        airport_id: null,
        direction: null,
      },
    }
  },

  computed: {
    contractHelpText() {
      return this.$t('company.contract_help_text', {
        url: this.contractTemplateUrl,
      })
    },

    showAirports() {
      return this.$allows('administer') && this.updating
    },
  },

  watch: {
    'model.divisions': {
      handler: function (newVal, oldVal) {
        if (newVal && oldVal) {
          this.fetchVehicles(newVal)
        }
      },
    },
  },

  mounted() {
    this.loadForm().then((result) => {
      this.createTimezone()
      this.user = result.user
      this.checked = result.companyVehicles
      this.vehicles = result.vehicles
      this.rideTypes = result.rideTypes
      this.contract = result.contract
      this.contractTemplateUrl = result.contract_template_url
      this.airports = result.company_airports

      this.setFieldGroup('contract', ['contract_id'])
    })
  },

  methods: {
    api() {
      const params = {
        division_id: this.currentDivision.id,
      }
      if (this.showAirports) {
        params['with'] = {
          airports: true,
        }
      }
      return new CompaniesApi().setParameters(params)
    },

    createTimezone() {
      for (var index in ListZones) {
        this.timezones.push({
          label: ListZones[index].text,
          value: ListZones[index].utc[0],
        })
      }
      this.fields.get('timezone').options = this.timezones
    },

    submit() {
      for (let rideType of this.rideTypes) {
        let attr = `vehicles[${rideType}]`
        this.model[attr] = this.checked[rideType]
        if (!this.inFieldList(this.fields.list, attr)) {
          this.fields.list.push(
            new Field({
              attribute: attr,
              value: this.checked[rideType],
            })
          )
        }
      }
      this.save().then((result) => {
        this.$toast.success(result.message)
      })
    },
    inFieldList(list, attr_name) {
      for (var i in list) {
        if (list[i].attribute === attr_name) return true
      }
      return false
    },
    checksIDs(arr) {
      let temp = []
      if (!arr.length) return temp
      for (var i in arr) temp.push(arr[i].id)
      return temp
    },

    onSaved(result) {
      this.contract = result.contract
      this.contractTemplateUrl = result.contract_template_url
    },

    fetchVehicles(divisionId) {
      if (divisionId.length === 0) {
        this.vehicles = []
        for (const rideType of Object.keys(this.checked)) {
          this.checked[rideType] = []
        }
        return
      }

      this.loading = true
      const vehiclesApi = new VehiclesApi({
        division_id: divisionId,
        include: 'division',
        per_page: 1000,
        context: 'checkbox',
      })

      vehiclesApi.all().then((response) => {
        this.vehicles = response.data
      })
    },

    addAirport(airport) {
      const api = new AirportsApi(airport.airport_id, { with: { data: true } })
      api
        .store({
          company_id: this.model.id,
          direction: airport.direction,
        })
        .then((response) => {
          this.$toast.success(response.message)
          this.airports.push({
            id: response.data.airport.id,
            name: response.data.airport.name,
            direction: response.data.direction,
            is_main: response.data.is_main,
          })
        })
    },

    deleteAirport(airport) {
      const api = new AirportsApi(airport.id)
      api.destroy(this.model.id).then((response) => {
        this.$toast.success(response.message)
        this.airports = this.airports.filter((item) => item.id !== airport.id)
      })
    },
  },
}
</script>

<style>
.service_areas .single-field {
  margin-bottom: 25px;
}
</style>
